/**
 * MedicationSchedule Component
 * 
 * A comprehensive medication management interface that displays medication schedules,
 * enables status updates, and provides different viewing options (daily, weekly, monthly).
 * 
 * This is the main export that implements the compound component pattern for better 
 * maintainability and performance. It uses virtualization to optimize rendering of 
 * 20-30 medications.
 * 
 * @author Meduler Team
 * @version 2.0.0
 */
import React, { lazy, memo, Suspense } from 'react';
import FixedMedicationScheduleContainer from './MedicationScheduleContainer';
import type { Medication } from '@/types/medication';

// Lazy-loaded components (below the fold)
const LazyLoadedSeoContent = lazy(() => import('../seo/MedicationSeoContent'));
const LazyMedicationAnalytics = lazy(() => 
  import('../analytics/MedicationAnalytics').then(module => ({ default: module.MedicationAnalytics }))
);
const LazyMotionComponent = lazy(() => 
  import('@/utils/optimizedMotion').then(module => ({ default: module.MotionComponent }))
);

/**
 * MedicationSchedule Interface
 * Defines the props for the MedicationSchedule component
 */
export interface MedicationScheduleProps {
  /** Optional demo mode flag */
  isDemo?: boolean;
  /** Optional initial medications data */
  initialMedications?: Medication[];
}

/**
 * MedicationSchedule Component
 * 
 * Main component for displaying and managing medication schedules.
 * This implementation uses the compound component pattern through
 * MedicationScheduleContainer for improved maintainability and performance.
 */
const MedicationSchedule: React.FC<MedicationScheduleProps> = (props) => {
  return <FixedMedicationScheduleContainer {...props} />;
};

// Memoize the component for better performance
const MemoizedMedicationSchedule = memo(MedicationSchedule);

// Export both default and named exports
export { MemoizedMedicationSchedule as MedicationSchedule };
export default MemoizedMedicationSchedule;
